import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { unsubscribe } from '#app/utils/user.server.ts'
import { type Route } from './+types/unsubscribe.ts'

const UnsubscribeSchema = z.object({ email: EmailSchema })

export async function loader({ request }: Route.LoaderArgs) {
	const baseUrl = new URL(request.url)
	const queryParams = baseUrl.searchParams
	const email = queryParams.get('email')
	return data({ email })
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = await parseWithZod(formData, {
		schema: UnsubscribeSchema,
		async: true,
	})
	if (submission.status !== 'success') {
		return data({
			email: null,
			status: submission.status,
			error: String(submission.reply()),
		})
	}
	const { email } = submission.value
	const response = await unsubscribe(email)

	if (response.status === 'error') {
		return redirectWithToast('.', {
			type: 'error',
			title: 'Error',
			description: `Failed to unsubscribe ${email}. ${response.error}`,
		})
	}

	if (response.updated === 0) {
		return redirectWithToast('.', {
			type: 'error',
			title: 'Error',
			description: `${email} not found to unsubscribe.`,
		})
	}

	return redirectWithToast('/', {
		type: 'success',
		title: 'Success',
		description: `${email} ${response.status}.`,
	})
}

export default function UnsubscribeRoute({ loaderData }: Route.ComponentProps) {
	const unsubscribe = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'unsubscribe-form',
		constraint: getZodConstraint(UnsubscribeSchema),
		defaultValue: {
			email: unsubscribe.data?.email ?? loaderData.email ?? '',
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: UnsubscribeSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container pb-32 pt-20">
			<div className="flex flex-col justify-center">
				<div className="text-center">
					<h1 className="text-h1">Unsubscribe</h1>
					<title>Unsubscribe from Clearwater Farms Unit 1 Emails</title>
					<p className="mt-3 text-body-md text-muted-foreground">
						Emails will be sent upon closure of irrigation schedules, at most
						once per week. <br />
						Email message will also be sent once a month to remind you of POA
						Meetings.
					</p>
				</div>
				<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
					<unsubscribe.Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<div>
							<Field
								labelProps={{
									htmlFor: fields.email.id,
									children: 'Email Address',
								}}
								inputProps={{
									autoFocus: true,
									...getInputProps(fields.email, { type: 'text' }),
								}}
								errors={fields.email.errors}
							/>
						</div>
						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="mt-6">
							<StatusButton
								className="w-full"
								status={
									unsubscribe.state === 'submitting'
										? 'pending'
										: (form.status ?? 'idle')
								}
								type="submit"
								disabled={unsubscribe.state !== 'idle'}
							>
								Unsubscribe
							</StatusButton>
						</div>
					</unsubscribe.Form>
					<Button asChild variant="outline" className="">
						<Link to="/" className="mt-2 text-center text-body-sm font-bold">
							<Icon name="arrow-left" className="mr-2" />
							Back to Home
						</Link>
					</Button>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
